import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ChatMessage, ChatSession, PageContext, WsIncoming } from '../types/chatbot'
import { ChatbotWebSocket, buildWsUrl } from '../api/chatbotApi'
import { useLocaleStore } from '@/stores/locale'
import { localStorageService, StorageKey } from '@/services/storage/localStorage.service'

let messageIdCounter = 0
function generateMessageId(): string {
  return `msg_${Date.now()}_${++messageIdCounter}`
}

// PersistedSession is what we keep in localStorage so the dialog survives a
// page reload or a WebSocket reconnect (DEV-212). `open` lets the widget
// re-open itself and re-attach to the conversation after a navigation away
// (e.g. following a link from the bot's answer) — DEV-213.
interface PersistedSession {
  sessionId: string
  visitorId: string
  open: boolean
}

export const useChatbotStore = defineStore('chatbot', () => {
  const localeStore = useLocaleStore()

  const isOpen = ref(false)
  const isConnected = ref(false)
  const isTyping = ref(false)
  const session = ref<ChatSession | null>(null)
  const messages = ref<ChatMessage[]>([])
  const error = ref<string | null>(null)
  const unreadCount = ref(0)
  const pageContext = ref<PageContext>({ type: 'general', pageName: 'home' })

  let ws: ChatbotWebSocket | null = null
  let streamingMessageId: string | null = null

  const hasSession = computed(() => !!session.value?.sessionId)

  function persistSession(): void {
    if (session.value?.sessionId) {
      localStorageService.set<PersistedSession>(StorageKey.CHATBOT_SESSION, {
        sessionId: session.value.sessionId,
        visitorId: session.value.visitorId,
        open: isOpen.value,
      })
    }
  }

  function loadPersistedSession(): PersistedSession | null {
    return localStorageService.get<PersistedSession>(StorageKey.CHATBOT_SESSION)
  }

  function clearPersistedSession(): void {
    localStorageService.remove(StorageKey.CHATBOT_SESSION)
  }

  // restoreIfOpen re-attaches to a persisted conversation on widget mount when
  // the chat was open before a navigation/reload, so following a link from the
  // bot's answer keeps the dialog (DEV-213). No-op for first-time visitors.
  function restoreIfOpen(): void {
    if (loadPersistedSession()?.open) {
      openChat()
    }
  }

  function openChat(): void {
    isOpen.value = true
    unreadCount.value = 0
    persistSession()

    if (!isConnected.value) {
      connectAndStartSession()
    }
  }

  function closeChat(): void {
    isOpen.value = false
    persistSession()
  }

  function updatePageContext(ctx: PageContext): void {
    pageContext.value = ctx
  }

  function connectAndStartSession(): void {
    if (ws) {
      ws.disconnect()
    }

    ws = new ChatbotWebSocket(buildWsUrl())

    ws.onConnect(() => {
      isConnected.value = true
      error.value = null
      startSession()
    })

    ws.onDisconnect(() => {
      isConnected.value = false
    })

    ws.onMessage(handleIncomingMessage)

    ws.connect()
  }

  function startSession(): void {
    if (!ws) return

    // Resume the persisted session if we have one so the backend reattaches to
    // the existing conversation instead of starting a fresh one.
    const persisted = loadPersistedSession()

    ws.send({
      type: 'start_session',
      session_id: persisted?.sessionId,
      language: localeStore.currentLocale,
      source: 'web-app',
      page_context: pageContext.value,
    })
  }

  function sendMessage(text: string): void {
    if (!ws || !session.value?.sessionId || !text.trim() || isTyping.value) return

    const userMsg: ChatMessage = {
      id: generateMessageId(),
      role: 'user',
      content: text.trim(),
      timestamp: Date.now(),
    }
    messages.value.push(userMsg)

    ws.send({
      type: 'send_message',
      session_id: session.value.sessionId,
      message: text.trim(),
      language: localeStore.currentLocale,
    })

    isTyping.value = true
  }

  function endSession(): void {
    if (ws && session.value?.sessionId) {
      ws.send({
        type: 'end_session',
        session_id: session.value.sessionId,
      })
    }

    if (ws) {
      ws.disconnect()
      ws = null
    }

    clearPersistedSession()
    session.value = null
    isConnected.value = false
    isTyping.value = false
    streamingMessageId = null
  }

  // disconnect tears down the socket WITHOUT ending the conversation: the
  // backend session and the persisted localStorage entry are kept so the dialog
  // can be resumed later. Used on widget unmount (page navigation/reload) — the
  // previous behaviour called endSession() here, which destroyed the session and
  // made the bot "forget" the conversation on every navigation (DEV-213).
  function disconnect(): void {
    if (ws) {
      ws.disconnect()
      ws = null
    }

    isConnected.value = false
    isTyping.value = false
    streamingMessageId = null
  }

  // applyServerHistory replaces the message list with the server-side history
  // (the source of truth) while keeping any local message that was not yet
  // persisted (e.g. a message typed right before a reconnect). Dedup is by
  // role+content so the same message is never shown twice. Incomplete streaming
  // messages are dropped — the server holds the final version.
  function applyServerHistory(serverMsgs: ChatMessage[]): void {
    const seen = new Set(serverMsgs.map(m => `${m.role}:${m.content.trim()}`))
    const localTail = messages.value.filter(
      m => !m.streaming && !seen.has(`${m.role}:${m.content.trim()}`),
    )
    messages.value = [...serverMsgs, ...localTail]
  }

  function handleIncomingMessage(msg: WsIncoming): void {
    switch (msg.type) {
      case 'session_started':
        session.value = {
          sessionId: msg.session_id || '',
          visitorId: msg.visitor_id || '',
          language: msg.language || localeStore.currentLocale,
        }
        persistSession()

        if (msg.messages && msg.messages.length > 0) {
          applyServerHistory(
            msg.messages.map(m => ({
              id: m.id || generateMessageId(),
              role: m.role as 'user' | 'assistant' | 'system',
              content: m.content,
              // Источники сохранённых сообщений тоже восстанавливаем, иначе
              // в резюмированной истории блок «Источники» пропадал (DEV-213).
              sourceDocs: m.source_documents,
              timestamp: Date.now(),
            })),
          )
        } else if (msg.greeting && messages.value.length === 0) {
          messages.value.push({
            id: generateMessageId(),
            role: 'assistant',
            content: msg.greeting,
            timestamp: Date.now(),
          })
        }
        break

      case 'chat_chunk':
        handleChatChunk(msg)
        break

      case 'session_ended':
        clearPersistedSession()
        session.value = null
        break

      case 'error':
        error.value = msg.error || 'Unknown error'
        isTyping.value = false
        break
    }
  }

  function handleChatChunk(msg: WsIncoming): void {
    if (!streamingMessageId) {
      streamingMessageId = generateMessageId()
      messages.value.push({
        id: streamingMessageId,
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        streaming: true,
      })
    }

    const idx = messages.value.findIndex(m => m.id === streamingMessageId)
    if (idx !== -1) {
      messages.value[idx].content += msg.content || ''

      if (msg.done) {
        messages.value[idx].streaming = false
        messages.value[idx].sourceDocs = msg.source_documents
        streamingMessageId = null
        isTyping.value = false

        if (!isOpen.value) {
          unreadCount.value++
        }
      }
    }
  }

  function reset(): void {
    endSession()
    messages.value = []
    error.value = null
    unreadCount.value = 0
    isOpen.value = false
  }

  return {
    isOpen,
    isConnected,
    isTyping,
    session,
    messages,
    error,
    unreadCount,
    pageContext,
    hasSession,
    openChat,
    closeChat,
    restoreIfOpen,
    disconnect,
    updatePageContext,
    sendMessage,
    endSession,
    reset,
  }
})
